import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronRight, FiLayout, FiMessageSquare, FiChevronDown } = FiIcons;

// Premium Dropdown mapped to body root coordinates
const PremiumSelect = ({ value, options, onChange, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({});
  const buttonRef = useRef(null);

  const handleOpen = () => {
    if (!isOpen) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < 200;

      setCoords({
        top: openUp ? undefined : rect.bottom + 6,
        bottom: openUp ? window.innerHeight - rect.top + 6 : undefined,
        left: rect.left,
        width: rect.width
      });
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className={`relative ${className}`}>
      <button
        ref={buttonRef}
        onClick={handleOpen}
        className="w-full bg-[#09090B] border border-white/10 rounded-lg px-3 py-2.5 text-xs text-white outline-none focus:border-zinc-500 font-medium flex items-center justify-between hover:bg-zinc-900/80 transition-colors shadow-sm"
      >
        <span className="truncate">{value}</span>
        <SafeIcon icon={FiChevronDown} className="text-zinc-500 shrink-0 ml-2" />
      </button>

      {isOpen && createPortal(
        <div className="fixed inset-0 z-[100]">
          <div className="absolute inset-0" onClick={() => setIsOpen(false)} />
          <div 
            className="absolute z-[101] bg-[#18181B] border border-white/10 rounded-lg shadow-[0_10px_40px_-5px_rgba(0,0,0,0.8)] py-1"
            style={{
              top: coords.top,
              bottom: coords.bottom,
              left: coords.left,
              minWidth: Math.max(140, coords.width)
            }}
          >
            {options.map(opt => (
              <button
                key={opt}
                onClick={() => { onChange(opt); setIsOpen(false); }}
                className={`w-full text-left px-3 py-2.5 text-xs font-medium transition-colors ${
                  value === opt ? 'bg-[#10B981]/10 text-[#10B981]' : 'text-zinc-300 hover:bg-white/5 hover:text-white'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default function DisplayTargets({ settings, updateSetting }) {
  const [openAccordion, setOpenAccordion] = useState(null);
  
  // Local state for instantaneous typing without lag
  const [inputs, setInputs] = useState({
    lobby_welcome: '',
    lobby_subtext: '',
    left_card_input_1: '',
    left_card_input_2: '',
    right_card_input_1: '',
    right_card_input_2: '',
    notice_header: '',
    notice_subtext: ''
  });

  const [focusedField, setFocusedField] = useState(null);
  const timeoutRefs = useRef({});

  // Clean up any pending timeouts if component unmounts
  useEffect(() => {
    return () => {
      Object.values(timeoutRefs.current).forEach(clearTimeout);
    };
  }, []);

  // Sync from props (settings) ONLY if not currently focused to prevent cursor jumping
  useEffect(() => {
    if (settings) {
      setInputs(prev => {
        let next = { ...prev };
        let changed = false;
        Object.keys(next).forEach(key => {
          if (focusedField !== key && settings[key] !== undefined && settings[key] !== next[key]) {
            next[key] = settings[key] || '';
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
  }, [settings, focusedField]);

  const handleChange = (key, value) => {
    // 1. Instant local update for a snappy UI
    setInputs(prev => ({ ...prev, [key]: value }));
    
    // 2. Debounced external update (Live sync while typing)
    if (timeoutRefs.current[key]) {
      clearTimeout(timeoutRefs.current[key]);
    }
    timeoutRefs.current[key] = setTimeout(() => {
      updateSetting(key, value);
    }, 400); // 400ms delay provides fluid "live" feel while saving to Supabase
  };

  const cardTypeOptions = ['Wi-Fi', 'QR Code', 'Info', 'None'];

  const toggle = (id) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };

  const handleTypeChange = (side, value) => {
    updateSetting(`${side}_card_type`, value);
    // Auto-clear inputs when switching types so it's a fresh slate (no leftover wi-fi names in QR codes)
    handleChange(`${side}_card_input_1`, '');
    handleChange(`${side}_card_input_2`, '');
  };

  const getPlaceholders = (type) => {
    switch (type) {
      case 'Wi-Fi': return ['Wi-Fi Network Name', 'Wi-Fi Password'];
      case 'QR Code': return ['Display Text (e.g., Leave a review)', 'Website Link (URL)'];
      case 'Info': return ['Main Headline', 'Description or Rules'];
      case 'None': return [null, null];
      default: return ['Headline', 'Details'];
    }
  };

  return (
    <div className="w-full pb-10">
      <div className="flex flex-col gap-3">
        
        {/* LOBBY DISPLAY */}
        <div className={`relative flex flex-col rounded-xl border border-white/5 bg-zinc-900/40 transition-all duration-300 ${openAccordion === 'lobby' ? 'z-20 shadow-lg' : 'z-10'}`}>
          <div 
            onClick={() => toggle('lobby')}
            className={`relative flex items-center justify-between p-4 hover:bg-zinc-800/50 transition-all duration-300 cursor-pointer group ${openAccordion === 'lobby' ? 'rounded-t-xl' : 'rounded-xl'}`}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-white/[0.03] to-transparent pointer-events-none rounded-inherit" />
            <div className="flex items-center gap-4 relative z-10">
              <div className="w-10 h-10 rounded-lg bg-zinc-800/50 border border-white/5 flex items-center justify-center text-zinc-500 group-hover:text-white group-hover:bg-zinc-800 transition-colors">
                <SafeIcon icon={FiLayout} className="text-lg" />
              </div>
              <div>
                <h3 className="font-semibold text-sm tracking-tight text-zinc-200 group-hover:text-white transition-colors">
                  Lobby Display
                </h3>
              </div>
            </div>
            <SafeIcon
              icon={FiChevronRight}
              className={`text-zinc-500 text-xl transition-transform duration-300 relative z-10 ${openAccordion === 'lobby' ? 'rotate-90 text-white' : ''}`}
            />
          </div>
          
          {/* Lobby Expanded Area */}
          {openAccordion === 'lobby' && (
            <div className="p-5 bg-zinc-950/50 border-t border-white/5 flex flex-col gap-6 rounded-b-xl">
              
              {/* Section 1: Screen Copy */}
              <div className="flex flex-col gap-3">
                <h4 className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest pl-1 mb-1">Screen Copy</h4>
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[11px] font-semibold text-zinc-400">Main Welcome Message</label>
                    <input 
                      type="text" 
                      value={inputs.lobby_welcome}
                      onChange={(e) => handleChange('lobby_welcome', e.target.value)}
                      onFocus={() => setFocusedField('lobby_welcome')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="e.g., Welcome In." 
                      className="bg-[#09090B] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-zinc-500 placeholder:text-zinc-600 font-medium transition-colors"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[11px] font-semibold text-zinc-400">Secondary Subtext (Optional)</label>
                    <input 
                      type="text" 
                      value={inputs.lobby_subtext}
                      onChange={(e) => handleChange('lobby_subtext', e.target.value)}
                      onFocus={() => setFocusedField('lobby_subtext')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="e.g., Make yourself comfortable." 
                      className="bg-[#09090B] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-zinc-500 placeholder:text-zinc-600 font-medium transition-colors"
                    />
                  </div>
                </div>
              </div>

              <div className="h-px w-full bg-white/5 rounded-full" />

              {/* Section 2: Display Cards */}
              <div className="flex flex-col gap-3">
                <h4 className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest pl-1 mb-1">Display Cards</h4>
                <div className="flex flex-col gap-3">
                  {[1, 2].map((row) => {
                    const side = row === 1 ? 'left' : 'right';
                    const currentType = settings?.[`${side}_card_type`] || (row === 1 ? 'Wi-Fi' : 'QR Code');
                    const [ph1, ph2] = getPlaceholders(currentType);

                    return (
                      <div key={row} className="flex gap-2 w-full">
                        <PremiumSelect 
                          value={currentType} 
                          options={cardTypeOptions} 
                          onChange={(val) => handleTypeChange(side, val)}
                          className="w-[140px] shrink-0" 
                        />
                        {currentType !== 'None' && (
                          <div className="flex gap-2 flex-1">
                            <input 
                              type="text" 
                              value={inputs[`${side}_card_input_1`]}
                              onChange={(e) => handleChange(`${side}_card_input_1`, e.target.value)}
                              onFocus={() => setFocusedField(`${side}_card_input_1`)}
                              onBlur={() => setFocusedField(null)}
                              placeholder={ph1} 
                              className="bg-[#09090B] border border-white/10 rounded-lg px-3 py-2.5 text-xs text-white flex-1 outline-none focus:border-zinc-500 placeholder:text-zinc-600 font-medium transition-colors"
                            />
                            <input 
                              type="text" 
                              value={inputs[`${side}_card_input_2`]}
                              onChange={(e) => handleChange(`${side}_card_input_2`, e.target.value)}
                              onFocus={() => setFocusedField(`${side}_card_input_2`)}
                              onBlur={() => setFocusedField(null)}
                              placeholder={ph2} 
                              className="bg-[#09090B] border border-white/10 rounded-lg px-3 py-2.5 text-xs text-white flex-1 outline-none focus:border-zinc-500 placeholder:text-zinc-600 font-medium transition-colors"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>
          )}
        </div>

        {/* NOTICE DISPLAY */}
        <div className={`relative flex flex-col rounded-xl border border-white/5 bg-zinc-900/40 transition-all duration-300 ${openAccordion === 'notice' ? 'z-20 shadow-lg' : 'z-10'}`}>
          <div 
            onClick={() => toggle('notice')}
            className={`relative flex items-center justify-between p-4 hover:bg-zinc-800/50 transition-all duration-300 cursor-pointer group ${openAccordion === 'notice' ? 'rounded-t-xl' : 'rounded-xl'}`}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-white/[0.03] to-transparent pointer-events-none rounded-inherit" />
            <div className="flex items-center gap-4 relative z-10">
              <div className="w-10 h-10 rounded-lg bg-zinc-800/50 border border-white/5 flex items-center justify-center text-zinc-500 group-hover:text-white group-hover:bg-zinc-800 transition-colors">
                <SafeIcon icon={FiMessageSquare} className="text-lg" />
              </div>
              <div>
                <h3 className="font-semibold text-sm tracking-tight text-zinc-200 group-hover:text-white transition-colors">
                  Notice Display
                </h3>
              </div>
            </div>
            <SafeIcon
              icon={FiChevronRight}
              className={`text-zinc-500 text-xl transition-transform duration-300 relative z-10 ${openAccordion === 'notice' ? 'rotate-90 text-white' : ''}`}
            />
          </div>
          
          {/* Notice Expanded Area */}
          {openAccordion === 'notice' && (
            <div className="p-5 bg-zinc-950/50 border-t border-white/5 flex flex-col gap-4 rounded-b-xl">
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-semibold text-zinc-400">Main Message</label>
                <input 
                  type="text" 
                  value={inputs.notice_header}
                  onChange={(e) => handleChange('notice_header', e.target.value)}
                  onFocus={() => setFocusedField('notice_header')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="e.g., Do Not Knock." 
                  className="bg-[#09090B] border border-white/10 rounded-lg px-4 py-3 text-sm text-white outline-none focus:border-zinc-500 placeholder:text-zinc-600 font-medium transition-colors"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-semibold text-zinc-400">Additional Context (Optional)</label>
                <textarea 
                  rows="2"
                  value={inputs.notice_subtext}
                  onChange={(e) => handleChange('notice_subtext', e.target.value)}
                  onFocus={() => setFocusedField('notice_subtext')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="e.g., Live recording in progress. Please use the back entrance." 
                  className="w-full bg-[#09090B] border border-white/10 rounded-lg px-4 py-3 text-sm text-white outline-none focus:border-zinc-500 placeholder:text-zinc-600 font-medium resize-none leading-relaxed transition-colors"
                ></textarea>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}